import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ErrorBoundary, type FallbackProps } from "react-error-boundary";

import { trackError } from "@lib/infrastructure";
import { Button } from "@molecules";

type HaNErrorBoundaryProps = {
  area: string;
  resetKeys?: unknown[];
  children: ReactNode;
};

/** Catches render errors in one part of H&N, so the rest keeps working, and reports them to App Insights. */
export const HaNErrorBoundary = ({ area, resetKeys, children }: HaNErrorBoundaryProps) => {
  return (
    <ErrorBoundary
      resetKeys={resetKeys}
      onError={error => trackError(error, { area: `HereAndNow_${area}` })}
      FallbackComponent={HaNErrorFallback}>
      {children}
    </ErrorBoundary>
  );
};

const HaNErrorFallback = ({ error, resetErrorBoundary }: FallbackProps) => {
  const { t } = useTranslation();

  return (
    <div
      role="alert"
      className="h-full flex flex-col items-center justify-center gap-3 p-6 text-center">
      <h2 className="m-0 text-base font-semibold">{t("error:Ooops")}</h2>
      <p className="m-0 text-sm text-mapiq-black-600">
        {error instanceof Error && error.message
          ? t("error:ErrorMessage", { errorText: error.message })
          : t("error:GenericError")}
      </p>
      <Button
        size="small"
        button="secondary"
        onClick={resetErrorBoundary}>
        {t("translation:Retry")}
      </Button>
    </div>
  );
};
